import React, { useEffect, useRef } from 'react'
import {
  Wrapper,
  Article,
  Image,
  AuthorImage,
  ArticleContent,
  Label,
  Title,
  Date,
  Paragraph,
  Unordered,
  Ordered,
  Link,
  ArticleSubTitle,
  ArticleSubTitle2,
  Video,
  Back,
  Quote,
  Next,
  ArticleImage,
} from '../../components/richTextOptions'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { ContentfulAsset, ContentfulArticle } from 'Types'
import { formatDate, formatPath } from 'sections/Resources/utils'
import placeholder from 'src/assets/images/resources.jpg'
import { ArrowButton } from 'components/Buttons'
import Form from 'src/templates/landingPageTemplates/Form'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

type Props = {
  article: ContentfulArticle
  articles: ContentfulArticle[]
  lastType: any
  lastCategory: any
  assets?: ContentfulAsset[]
}
const Content: React.FC<Props> = ({
  article,
  articles,
  lastType,
  lastCategory,
}) => {
  const articleRef = useRef(null)
  const nextArticle = [...articles].filter((e) => e.title !== article.title)[0]

  const { references } = article.content

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph>{children}</Paragraph>,
      [BLOCKS.UL_LIST]: (node, children) => <Unordered>{children}</Unordered>,
      [BLOCKS.OL_LIST]: (node, children) => <Ordered>{children}</Ordered>,
      [BLOCKS.QUOTE]: (node, children) => <Quote>{children}</Quote>,
      [INLINES.HYPERLINK]: (node, children) => {
        if (
          node.data.uri.toLowerCase().includes('vimeo') ||
          node.data.uri.toLowerCase().includes('youtube')
        ) {
          return <Video src={node.data.uri} frameBorder="0" />
        } else {
          return <Link href={node.data.uri}>{children}</Link>
        }
      },
      [BLOCKS.HEADING_1]: (node, children) => (
        <ArticleSubTitle className="article-sub__1">{children}</ArticleSubTitle>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <ArticleSubTitle2 className="article-sub__2">
          {children}
        </ArticleSubTitle2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <ArticleSubTitle>{children}</ArticleSubTitle>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <ArticleSubTitle>{children}</ArticleSubTitle>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <ArticleSubTitle>{children}</ArticleSubTitle>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <ArticleSubTitle>{children}</ArticleSubTitle>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const id = node.data.target.sys.id
        const src = references.filter((e) => e.contentful_id === id)[0].file.url

        return <ArticleImage src={src} alt="" />
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const id = node.data.target.sys.id
        const form = references.filter((e) => e.contentful_id === id)[0]

        return <Form data={{ ...form }} inline />
      },
    },
    renderText: (text: string) => text.replace('!', '?'),
  }
  useEffect(() => {
    if (articleRef.current) {
      const allH2 = articleRef.current.querySelectorAll('.article-sub__2')
      allH2.forEach((h2) => {
        if (h2.previousElementSibling.className.includes('article-sub__1')) {
          h2.style.marginTop = 0
        } else {
          return
        }
      })
    }
  }, [])
  return (
    <Wrapper ref={articleRef}>
      <Back to="/resources" state={{ type: lastType, category: lastCategory }}>
        <ArrowButton left>Resources</ArrowButton>
      </Back>
      <Article>
        {!article.hideCoverImage && (
          <Image
            src={
              article.coverImage
                ? article.coverImage.file
                  ? article.coverImage.file.url
                  : placeholder
                : placeholder
            }
          />
        )}
        <ArticleContent>
          <Label bullet>{article.category.split('-')[0]}</Label>
          <Title>{article.title}</Title>
          <Date>
            {article.author && (
              <AuthorImage src={article.author.photo.file.url} />
            )}
            {article.author && (
              <span>
                <strong>{article.author.name}</strong> {` `}|
              </span>
            )}

            {formatDate(article.publishedDate)}
          </Date>

          {
            //@ts-ignore
            documentToReactComponents(JSON.parse(article.content.raw), options)
          }
        </ArticleContent>
      </Article>
      <Next to={`/${formatPath(nextArticle.title)}`}>
        <ArrowButton>Next Article</ArrowButton>
      </Next>
    </Wrapper>
  )
}

export default Content
